import { ReportedEvent } from '../reporting';

export interface BrandConfig {
  createdAt: Date;
  updatedAt: Date;
  updatedBy: number;
  id: number;
  brandId: number;
  showWelcomeMessage: boolean;
  welcomeMessage: string;
  widgetLogoUri: string;
  widgetTitle: string;
  new: boolean;
  createdBy: number;
}

export interface BrandCategory {
  id: number;
  title: string;
  iconUri: string;
  showToEndUser: boolean;
}

export interface BrandConfigDetail {
  id: number;
  name: string;
  brandConfig: BrandConfig;
  categories: BrandCategory[];
}

export interface ApiResponse<T> {
  code: number;
  message: string;
  total: number;
  data: T;
}

export interface BrandConfigResponse extends ApiResponse<BrandConfigDetail> { }

export interface SectionConfig {
  createdAt: Date;
  updatedAt: Date;
  updatedBy: number;
  id: number;
  sectionId: number;
  noneOfTheAbove: boolean;
  connectToAgent: boolean;
  loginRequired: boolean;
  showOpenRequest: boolean;
  showLiveChat: boolean;
  showVoiceChat: boolean;
  showResponse: boolean;
  responseMessage: string;
  transactionMessage: string;
  showEndMessage: boolean;
  endMessage: string;
  showToEndUser: boolean;
  new: boolean;
  createdBy: number;
  overrideParentConfig: boolean;
}

export interface NoneOfTheAboveConfig {
  createdAt: Date;
  updatedAt: Date;
  updatedBy: number;
  id: number;
  noneOfTheAbove: boolean;
  connectToAgent: boolean;
  loginRequired: boolean;
  showOpenRequest: boolean;
  showLiveChat: boolean;
  showVoiceChat: boolean;
  showResponse: boolean;
  responseMessage: string;
  transactionMessage: string;
  showEndMessage: boolean;
  endMessage: string;
  showToEndUser: boolean;
  new: boolean;
  createdBy: number;
  overrideParentConfig: boolean;
}

export interface Article {
  id: number;
  title: string;
  showToEndUser: boolean;
  checks: string|null;
  action: string|null;
  actionMessage: string|null;
}

export interface SearchArticle {
  id: number;
  brandId: number;
  sectionId: number;
  categoryId: number;
  categoryName: string;
  categoryNameTranslated: string;
  title: string;
  titleTranslated: string;
  position: number;
  isDraft: boolean;
  isPromoted: boolean;
  isActive: boolean;
  language: string;
  englishVersion: boolean;
  updatedAt: string;
}

export interface Section {
  id: number;
  title: string;
  showToEndUser: boolean;
}

export interface SectionDetail {
  id: number;
  title: string;
  sectionConfig: SectionConfig;
  noneOfTheAboveConfig: NoneOfTheAboveConfig & { sectionId: number | null };
  articles: Article[];
  sections: Section[];
}

export interface CategoryConfig {
  createdAt: string;
  updatedAt: string;
  updatedBy: number;
  id: number;
  categoryId: number;
  noneOfTheAbove: boolean;
  showResponse: boolean;
  responseMessage: string;
  iconUri: string;
  showToEndUser: boolean;
  showEndMessage: boolean;
  endMessage: string;
  showLiveChat: boolean;
  showOpenRequest: boolean;
  showVoiceChat: boolean;
  transactionMessage: string;
  connectToAgent: boolean;
  loginRequired: boolean;
  new: boolean;
  createdBy: number;
}

export interface CategoryDetail {
  id: number;
  title: string;
  categoryConfig: CategoryConfig;
  noneOfTheAboveConfig: NoneOfTheAboveConfig & { categoryId: number | null };
  sections: Section[];
  articles: Article[];
}

export interface ArticleConfig {
  createdAt: Date;
  updatedAt?: any;
  updatedBy?: any;
  id: number;
  articleId: number;
  showToEndUser: boolean;
  showResponse: boolean;
  responseMessage: string;
  showDescriptionBox: boolean;
  descriptionMessage: string;
  new: boolean;
  createdBy: number;
  action?: string|null;
  actionMessage: string|null;
  actionText?: string|null;
  checks?: string;
  transactionType?: null |string;
}

export interface ArticleParentConfig {
  id: number;
  type: string;
  title: string;
  showToEndUser?: boolean;
  language: string;
  connectToAgent: boolean;
  loginRequired?: boolean;
  showLiveChat?: boolean;
  showOpenRequest?: boolean;
  showVoiceChat?: boolean;
  transactionMessage?: string;
}

export interface ArticleDetailCategory {
  brandId: number;
  description: string;
  descriptionTranslated: string;
  englishVersion: boolean;
  id: number;
  isActive: boolean;
  language: string;
  position: number;
  title: string;
  titleTranslated: string;
}

export interface ArticleDetail {
  id: number;
  title: string;
  body: string;
  articleConfig: ArticleConfig;
  category: ArticleDetailCategory;
  section?: ArticleDetailSection;
}

export interface ArticleDetailSection {
  id: number;
  brandId: number;
  categoryId: number;
  parentSectionId?: number;
  position: number;
  title: string;
  description: string;
  titleTranslated?: any;
  descriptionTranslated?: any;
  sections?: any;
  articles?: any;
  isActive: boolean;
  language: string;
  englishVersion: boolean;
}

export enum ArticleRate {
  HELPFUL = 'HELPFUL',
  NOT_HELPFUL = 'NOT_HELPFUL'
}

export interface ArticleRating {
  rating: ArticleRate | null;
}

export interface TicketField {
  fieldId: number;
  fieldValue: string;
}

export interface TicketFieldDetail {
  createdAt: Date;
  updatedAt: Date;
  updatedBy: number;
  id: number;
  displayName: string;
  placeholder: string;
  fieldName: string;
  isSystemField: boolean;
  fieldType: string;
  fieldValueOptions?: any;
  isMandatory: boolean;
  isUserEditable: boolean;
  isActive: boolean;
  fieldValueOptionsList?: any[];
  new: boolean;
  createdBy: number;
}

export interface TicketCreateRequest {
  subject: string;
  description: string;
  recipientAddress: string;
  categoryId?: number;
  ticketFields: TicketField[];
  userSelections?: {message: string; createdAt: string}[];
  userLogs?: ReportedEvent[]
}

export interface ExtraField {
  id: number;
  ticketId: number;
  fieldId: number;
  displayName: string;
  fieldName: string;
  fieldValue: string;
  isMandatory: boolean;
  isActive: boolean;
}

export interface Author {
  createdAt: Date;
  updatedAt: Date;
  updatedBy: number;
  id: number;
  foreignId?: any;
  email: string;
  phone: string;
  name: string;
  imageUrl?: any;
  roleId: number;
  sourceId: number;
  isActive: boolean;
  extraFields: Pick<ExtraField, 'fieldName' | 'fieldValue'> & Partial<ExtraField>[];
  defaultName: string;
  new: boolean;
  createdBy: number;
}

export interface TicketAttachment {
  createdAt: Date;
  updatedAt: Date;
  updatedBy: number;
  id: number;
  fileName: string;
  url: string;
  contentType: string;
  isActive: boolean;
  isInline: boolean;
  ticketCommentId: number;
  new: boolean;
  createdBy: number;
}

export interface Comment {
  createdAt: Date;
  updatedAt: Date;
  updatedBy: number;
  id: number;
  body: string;
  isPublic: boolean;
  sourceId: number;
  authorId: number;
  ticketId: number;
  attachments?: TicketAttachment[];
  author: Author;
  sourceName?: any;
  new: boolean;
  createdBy: number;
  bodyHtml: string;
}

export interface TicketDetail {
  createdAt: Date;
  updatedAt: Date;
  updatedBy: number;
  id: number;
  subject: string;
  emailSubject: string;
  priority: string;
  status: string;
  recipientAddress: string;
  receivedAddress: string;
  requesterId: number;
  assigneeId?: any;
  groupId?: any;
  emailCCIds?: any;
  slaTimestamp?: any;
  sourceId: number;
  rating?: any;
  brandId: number;
  categoryId: number;
  isAgentViewed?: any;
  extraFields: ExtraField[];
  comments: Comment[];
  tags?: any;
  new: boolean;
  createdBy: number;
}

export interface QueuePosition {
  position: number;
}

export interface UserInformation {
  createdAt: Date;
  updatedAt: Date;
  updatedBy: number;
  id: number;
  foreignId: string;
  email?: string;
  phone: string;
  name?: string;
  imageUrl?: string;
  roleId: number;
  sourceId: number;
  isActive: boolean;
  extraFields: any[];
  token: string;
  defaultName: string;
  new: boolean;
  createdBy: number;
}

export interface AgentsOnline {
  onlineAgents: boolean;
}

export interface OnlineAgentsCheck {
  onlineChatAgentsCheck?: boolean;
  onlineCallAgentsCheck?: boolean;
}

export interface TicketListQueryParams {
  requesterId: number | string,
  offset: number,
  limit: number,
  order: 'ASC' | 'DESC',
  sortBy: 'STATUS' | 'PRIORITY' | 'CREATED_AT' | 'UPDATED_AT' | 'SUBJECT' | 'REQUESTER'
}

export interface ListTicketDetail {
  createdAt: Date;
  updatedAt: Date;
  updatedBy: number;
  id: number;
  subject: string;
  emailSubject: string;
  priority: string;
  status: string;
  recipientAddress?: any;
  receivedAddress?: any;
  requesterId: number;
  assigneeId: number;
  groupId?: any;
  emailCCIds?: any;
  slaTimestamp?: any;
  sourceId: number;
  rating?: any;
  brandId: number;
  categoryId?: any;
  isAgentViewed?: any;
  requesterName: string;
  requesterPhone: string;
  requesterEmail: string;
  agentName: string;
  agentEmail: string;
  groupName?: any;
  extraFields?: any;
  comments?: any;
  new: boolean;
  createdBy: number;
}

export interface ChatOverlayTrigger {
  message: string;
  chatTriggerId: number;
}

export interface CategoryDetailResponse extends ApiResponse<CategoryDetail> { }

export interface SectionDetailResponse extends ApiResponse<SectionDetail> { }

export interface ArticleDetailResponse extends ApiResponse<ArticleDetail> { }

export interface RelatedArticlesResponse extends ApiResponse<SearchArticle[]> { }

export interface ArticleParentConfigResponse extends ApiResponse<ArticleParentConfig> { }

export interface ArticleSearchResponse extends ApiResponse<SearchArticle[]> { }

export interface ArticleRatingResponse extends ApiResponse<ArticleRating> { }

export interface TicketCreateResponse extends ApiResponse<TicketDetail> { }

export interface TicketFieldsResponse extends ApiResponse<TicketFieldDetail[]> { }

export interface QueuePositionResponse extends ApiResponse<QueuePosition> { }

export interface UserInformationResponse extends ApiResponse<UserInformation> { }

export interface AgentsOnlineResponse extends ApiResponse<AgentsOnline> { }

export interface OnlineAgentsCheckResponse extends ApiResponse<OnlineAgentsCheck> { }

export interface TicketListResponse extends ApiResponse<ListTicketDetail[]> { }

export interface AddAttachmentResponse extends ApiResponse<TicketAttachment> { }

export interface ChatOverlayTriggerResponse extends ApiResponse<ChatOverlayTrigger> { }

export interface ActiveTicketVoice {
  createAt: string | Date | number;
  duration: number;
  voiceChatRecordingUrl: string;
}

export interface ActiveTicketAttachment {
  fileName: string;
  url: string;
  contentType: string;
}
export interface ActiveTicketComment {
  commentId: number;
  attachments?: ActiveTicketAttachment[];
  authorName: string;
  authorType: 'AGENT' | 'CUSTOMER';
  body: string;
  bodyHtml: string;
  source: 'LIVE_CHAT' | 'VOICE_CHAT'
  createAt: string | Date | number;
  voice?: ActiveTicketVoice;
}
export interface ActiveTicket {
  id: number;
  subject: string;
  emailSubject: string;
  status: string;
  updatedAt: string | Date | number;
  commentCount: number;
  latestComment?: ActiveTicketComment;
}

export interface ActiveComments {
  comments: ActiveTicketComment[];
  ticket: ActiveTicket;
}

export interface ActiveTicketResponse extends ApiResponse<ActiveTicket[]> { };
export interface ActiveTicketCommentsResponse extends ApiResponse<ActiveComments> { };

type WaitTimes = '<1' | '<3' | '<5' | '<10'
export interface WaitingTime {
  averageChatWaitingTime: WaitTimes|null,
  averageCallWaitingTime: WaitTimes|null
}

export interface WaitTimeResponse extends ApiResponse<WaitingTime> {}

export interface ActiveOpenChat {
  id: number;
  chatStatus?: any;
  firebaseToken: string;
  customerId: number;
  customerName?: string;
  subject: string;
  assigneeId: number;
  assigneeName?: null|string;
  groupId?: null|number;
  brandId: number;
  brandName?: null|string;
  duration?: null|number;
  ongoingDuration?: null|number;
  ticketId?: null|number;
}

export interface VerifyChatOpenDto {
  canResume: boolean;
  ticketId?: null|number;
  inActiveConversation: boolean;
}
export interface ActiveOpenChatResponse extends ApiResponse<ActiveOpenChat[]> {}

export interface CheckChatOpenResponse extends ApiResponse<VerifyChatOpenDto> {}
export interface FeedbackCategory {
  feedbackSelectionId: number;
  name: string;
}

export interface FeedbackCategoryResponse extends ApiResponse<FeedbackCategory[]> {}

export interface VoiceChatCsatResponse extends ApiResponse<boolean> {}
